import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Camera, CheckCircle, FileText, Loader2, RefreshCw, Trash2, AlertTriangle, IdCard } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const SIDE_LABEL = { front: "Front", back: "Back" };

function Slot({ kind, token, doc, side, file, rejection, onChange }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const slotId = `${doc.key}-${side}`;
  const isImage = file?.content_type?.startsWith("image/");

  const upload = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    if (f.size > 15 * 1024 * 1024) return toast.error("Please keep each photo under 15 MB");
    setBusy(true);
    try {
      const form = new FormData();
      form.append("file", f);
      form.append("doc_key", doc.key);
      form.append("side", side);
      const r = await axios.post(`${API}/identity-docs/${kind}/${token}/upload`, form, { headers: { "Content-Type": "multipart/form-data" } });
      onChange(r.data);
      toast.success(`${SIDE_LABEL[side]} of ${doc.label.split("(")[0].trim()} uploaded`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed — please try again");
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    setBusy(true);
    try {
      const r = await axios.delete(`${API}/identity-docs/${kind}/${token}/${doc.key}/${side}`);
      onChange(r.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not remove");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className={`rounded-lg border p-3 flex flex-col gap-2 ${file ? "border-emerald-200 bg-emerald-50/40" : rejection ? "border-red-200 bg-red-50/40" : "border-slate-200 bg-white"}`} data-testid={`identity-slot-${slotId}`}>
      <div className="flex items-center justify-between">
        <span className="text-xs font-medium text-slate-600">{doc.sides.length > 1 ? SIDE_LABEL[side] : "Photo"}</span>
        {file && <Badge variant="outline" className="text-[10px] gap-1 bg-emerald-50 text-emerald-700 border-emerald-200"><CheckCircle className="w-3 h-3" /> {file.review_status === "approved" ? "Approved" : "Uploaded"}</Badge>}
      </div>
      <input ref={inputRef} type="file" accept="image/*,.heic,.heif,application/pdf" className="hidden" onChange={upload} data-testid={`identity-input-${slotId}`} />
      {file ? (
        <div className="space-y-2">
          <div className="h-32 rounded-md bg-slate-100 overflow-hidden flex items-center justify-center">
            {isImage ? (
              <img src={`${API}/identity-docs/${kind}/${token}/file/${file.id}`} alt={doc.label} className="h-full w-full object-contain" data-testid={`identity-preview-${slotId}`} />
            ) : (
              <div className="text-slate-500 text-xs flex flex-col items-center gap-1"><FileText className="w-6 h-6" /> {file.filename}</div>
            )}
          </div>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" className="flex-1 gap-1" disabled={busy} onClick={() => inputRef.current?.click()} data-testid={`identity-retake-${slotId}`}>
              {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />} Retake
            </Button>
            {file.review_status !== "approved" && (
              <Button size="sm" variant="ghost" className="text-red-600" disabled={busy} onClick={remove} data-testid={`identity-remove-${slotId}`}><Trash2 className="w-3.5 h-3.5" /></Button>
            )}
          </div>
        </div>
      ) : (
        <>
          {rejection && <p className="text-xs text-red-700 flex items-start gap-1"><AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" /> Previous upload was rejected{rejection !== true && rejection ? `: ${rejection}` : ""}. Please upload again.</p>}
          <Button onClick={() => inputRef.current?.click()} disabled={busy} className="w-full h-28 flex-col gap-2 bg-slate-900 hover:bg-slate-800 text-white" data-testid={`identity-upload-${slotId}`}>
            {busy ? <Loader2 className="w-6 h-6 animate-spin" /> : <Camera className="w-6 h-6" />}
            <span className="text-xs">Take photo or upload</span>
          </Button>
        </>
      )}
    </div>
  );
}

export default function IdentityDocsStep({ kind, token, onStatus }) {
  const [status, setStatus] = useState(null);
  const onStatusRef = useRef(onStatus);
  onStatusRef.current = onStatus;

  const apply = useCallback((s) => { setStatus(s); onStatusRef.current?.(s); }, []);

  useEffect(() => {
    axios.get(`${API}/identity-docs/${kind}/${token}`).then((r) => apply(r.data)).catch(() => {});
  }, [kind, token, apply]);

  if (!status || status.documents.length === 0) return null;
  const complete = status.missing_required.length === 0;
  const docByKey = Object.fromEntries(status.documents.map((d) => [d.key, d]));
  const units = status.requirements || [];
  const listAUnits = units.filter((u) => u.kind === "doc" && docByKey[u.id]?.i9_list === "a");
  const otherUnits = units.filter((u) => !listAUnits.includes(u));
  const listACovers = otherUnits.some((u) => u.list_a_eligible);

  const renderDoc = (doc, { dimmed = false, note = null } = {}) => (
    <div key={doc.key} className={`space-y-2 ${dimmed ? "opacity-50" : ""}`} data-testid={`identity-doc-${doc.key}`}>
      <div className="flex items-center gap-2">
        <IdCard className="w-4 h-4 text-teal-600" />
        <p className="font-medium text-slate-900 text-sm">{doc.label}</p>
        {note && <span className="text-[10px] text-slate-500" data-testid={`identity-doc-note-${doc.key}`}>{note}</span>}
      </div>
      <div className="grid gap-3 sm:grid-cols-2">
        {doc.sides.map((side) => (
          <Slot key={side} kind={kind} token={token} doc={doc} side={side}
            file={status.uploaded[`${doc.key}:${side}`]} rejection={`${doc.key}:${side}` in status.rejected ? (status.rejected[`${doc.key}:${side}`] || true) : null}
            onChange={apply} />
        ))}
      </div>
    </div>
  );

  const renderUnit = (u) => {
    const docs = u.doc_keys.map((k) => docByKey[k]).filter(Boolean);
    const chosen = u.satisfied_by[0];
    return (
      <div key={u.id} className={`rounded-xl border p-4 space-y-4 ${u.satisfied ? "border-emerald-200 bg-emerald-50/30" : "border-slate-200"}`} data-testid={`identity-unit-${u.id}`}>
        <div className="flex flex-wrap items-center gap-2">
          <p className="font-semibold text-slate-900 text-sm">{u.kind === "group" ? u.label : docs[0]?.label}</p>
          {u.required ? <Badge variant="outline" className="text-[10px]">Required</Badge> : <span className="text-[10px] text-slate-400">Optional</span>}
          {u.satisfied && <Badge variant="outline" className="text-[10px] gap-1 bg-emerald-50 text-emerald-700 border-emerald-200"><CheckCircle className="w-3 h-3" /> Done</Badge>}
        </div>
        {u.kind === "group" && u.covered_by_list_a && (
          <p className="text-xs text-emerald-700" data-testid={`identity-unit-covered-${u.id}`}>Covered by your List A document — nothing else needed here.</p>
        )}
        {u.kind === "group" && !u.covered_by_list_a && docs.length > 1 && (
          <p className="text-xs text-slate-600">Upload <span className="font-medium">one</span> of the following: {u.choices}.</p>
        )}
        {u.kind === "group"
          ? docs.map((doc) => renderDoc(doc, {
              dimmed: (chosen && chosen !== doc.key) || u.covered_by_list_a,
              note: chosen && chosen !== doc.key ? `Not needed — you provided your ${docByKey[chosen].label.split("(")[0].trim()}` : null,
            }))
          : docs.map((doc) => renderDoc(doc))}
      </div>
    );
  };

  return (
    <div className="space-y-4" data-testid="identity-docs-step">
      <p className="text-sm text-slate-600">
        Use your phone camera or upload a clear photo. Make sure all four corners are visible and the text is readable. Accepted: JPG, PNG, HEIC or PDF, up to 15 MB each.
      </p>
      {listAUnits.length > 0 && (
        <div className="rounded-xl border border-teal-200 bg-teal-50/40 p-4 space-y-4" data-testid="identity-list-a-section">
          <p className="text-sm text-teal-900">
            {listACovers ? "Have a U.S. passport or Permanent Resident card? Upload it here and you can skip the documents below." : "Optional List A document."}
          </p>
          {listAUnits.map((u) => renderDoc(docByKey[u.id]))}
        </div>
      )}
      {otherUnits.map(renderUnit)}
      <p className={`text-xs flex items-center gap-1 ${complete ? "text-emerald-700" : "text-amber-700"}`} data-testid="identity-docs-status">
        {complete ? <><CheckCircle className="w-3.5 h-3.5" /> All required documents uploaded</> : <><AlertTriangle className="w-3.5 h-3.5" /> Still needed: {status.missing_required.join("; ")}</>}
      </p>
    </div>
  );
}
